import React from 'react';

export type CheckboxChangedEvent = {
  target: {
    checked: boolean;
  };
};

export type BaseResponse<T> = {
  data: T;
};

export type BodyResponse<T> = {
  data: { body: T };
};

export const callbackHooks = {
  useDebounce(callback: (...params: unknown[]) => unknown, timeoutMs: number) {
    const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
    return React.useCallback((...params: unknown[]) => {
      if (timer) {
        clearTimeout(timer);
      }
      const updatedTimer = setTimeout(() => {
        callback(...params);
      }, timeoutMs);
      setTimer(updatedTimer);
    }, [callback, timeoutMs, timer, setTimer]);
  },

  useWrapEventHandlers<
    TEvent extends React.SyntheticEvent,
    TEventhandler extends React.EventHandler<TEvent>
  >(
    callbacks: TEventhandler[],
    stopPropagation?: boolean,
  ) {
    return React.useCallback((e: TEvent) => {
      callbacks.forEach(callback => callback(e));
      if (stopPropagation) {
        e.stopPropagation();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...callbacks, stopPropagation])
  },

  useWrapCallbacks<TArgs>(
    callbacks: ((...args: TArgs[]) => void)[],
  ) {
    return React.useCallback((...args: TArgs[]) => {
      callbacks.forEach(callback => callback(...args));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...callbacks])
  },

  useInvertBoolean(callback: (value: boolean) => void) {
    return React.useCallback((value: boolean) => {
      callback(!value);
    }, [callback]);
  },

  useStopPropagation() {
    return React.useCallback((e: React.SyntheticEvent) => {
      e.stopPropagation();
    }, []);
  },

  useSetValueFromInput<TValue extends string>(setValue: (value: TValue) => void) {
    return React.useCallback(
      (e: { target: { value: string } }) => {
        setValue(e.target.value as TValue);
      }, [setValue]);
  },

  useSetValueFromCheckbox(setValue: (value: boolean) => void) {
    return React.useCallback(
      (e: CheckboxChangedEvent) => {
        setValue(e.target.checked);
      }, [setValue]);
  },

  useEffectCallback(callback: () => unknown) {
    React.useEffect(() => {
      callback();
    }, [callback]);
  },

  useResponseData<T>(callback: (data: T | null) => void) {
    return React.useCallback((response: BaseResponse<T> | null) => {
      callback(response?.data || null);
    }, [callback]);
  },
  useResponseBody<T>(callback: (data: T | null) => void) {
    return React.useCallback((response: BodyResponse<T> | null) => {
      callback(response?.data.body || null);
    }, [callback]);
  },
};
